import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import {
    IContextState,
    IContextProps,
    IEventsEditionsPending,
    IManualQualityPendingEntry,
    buildManualQualityPendingKey,
} from "./HMICachedDataContextInterfaces";
import { HMICachedDataContext, initialStateHMICachedDataContext } from "./HMICachedDataContext";
import {
    getClusterIdHMI,
    getEditionsPendingHMIStore,
    getMachineCenterHMIStore,
    getMachineCenterIdHMI,
    getMQTTBrokerSettingsHMIStore,
    getManualQualityPendingHMIStore,
    isTokenAvailable,
} from "../../utilities/storageUtilities";
import { HMI_PATHS, UPDATEMINUTESRATEFORHMIDATA } from "constants/constants";
import { setLocalStorageItem } from "utilities/storageUtilities";
import {
    IClusterMachineCenter,
    IClusterStore,
} from "contexts/ClusterCachedDataContext/ClusterCachedDataContextInterfaces";
import { LocalStorageCachedDataKeysHMI } from "constants/constants";
import { getInfoFromCloudserver, getInfoFromLocalserver, setCachedData } from "./helpers";
import useHMIHeartbeatSignal from "hooks/useHMIHeartbeatSignal/useHMIHeartbeatSignal";
import { HeartbeatMode } from "hooks/useHMIHeartbeatSignal/interfaces";

export const HMICachedDataContextProvider: React.FC<IContextProps> = ({ children }) => {
    const [store, setStore] = useState<IContextState["store"]>(initialStateHMICachedDataContext.store);

    const GetData = useCallback(async (machine_center_id: number, login?: boolean) => {
        try {
            const machineCenterLocalStorage = getMachineCenterHMIStore();
            const mqttBrokerSettingsLocalStorage = getMQTTBrokerSettingsHMIStore();

            if (login) {
                await getInfoFromCloudserver(machine_center_id, setStore);
            } else {
                if (
                    mqttBrokerSettingsLocalStorage?.[0] &&
                    machineCenterLocalStorage?.production_line?.mill?.dedicated_localserver
                ) {
                    try {
                        await getInfoFromLocalserver(machine_center_id, setStore, mqttBrokerSettingsLocalStorage?.[0]);
                    } catch {
                        await getInfoFromCloudserver(machine_center_id, setStore);
                    }
                } else {
                    await getInfoFromCloudserver(machine_center_id, setStore);
                }
            }
        } catch (error: any) {
            setCachedData(setStore, error);
            throw error;
        }
    }, []);

    const resetStore: IContextState["dispatch"]["resetStore"] = useCallback(
        () =>
            setStore({
                ...initialStateHMICachedDataContext.store,
            }),
        [],
    );

    const editEventReportData: IContextState["dispatch"]["editEventReportData"] = useCallback(
        (row) =>
            setStore((prevStore) => ({
                ...prevStore,
                editEventReportData: { row },
                isEventReportEditing: true,
            })),
        [],
    );

    const isEventReportEditing: IContextState["dispatch"]["isEventReportEditing"] = useCallback(
        (isEditing) =>
            setStore((prevStore) => ({
                ...prevStore,
                isEventReportEditing: isEditing,
            })),
        [],
    );

    const handleMoveToCluster = useCallback((clusterStore: IClusterStore, machineCenterId: number) => {
        try {
            const machineCenterCluster = clusterStore.machineCentersCluster.find(
                (machineCenterCluster) => machineCenterCluster.machine_center_id === machineCenterId,
            ) as IClusterMachineCenter;
            const hmiCompressedData = clusterStore.compressedHMIData[machineCenterId];
            const editionsPending = getEditionsPendingHMIStore() || {};
            const manualQualityPending = getManualQualityPendingHMIStore() || {};

            const {
                last_two_shifts_event_reports,
                non_location_specific_failure_codes,
                failure_types,
                ...machineCenter
            } = machineCenterCluster;

            const cachedStore = {
                machineCenter: machineCenter,
                failureTypes: failure_types || [],
                lastUpdated: clusterStore.lastUpdated,
                mqttBrokerSettings: clusterStore.mqttBrokerSettings,
                lastTwoShiftsEventReports: last_two_shifts_event_reports,
                editionsPending: editionsPending,
                manualQualityPending: manualQualityPending,
            };
            if (hmiCompressedData?.machineCenterInfo) {
                localStorage.setItem(LocalStorageCachedDataKeysHMI.MACHINE_CENTER, hmiCompressedData.machineCenterInfo);
            } else {
                setLocalStorageItem(LocalStorageCachedDataKeysHMI.MACHINE_CENTER, JSON.stringify(machineCenter));
            }
            localStorage.setItem(LocalStorageCachedDataKeysHMI.FAILURE_TYPES, JSON.stringify(failure_types || []));
            localStorage.setItem(
                LocalStorageCachedDataKeysHMI.DATA_LAST_UPDATED,
                JSON.stringify(clusterStore.lastUpdated),
            );
            localStorage.setItem(
                LocalStorageCachedDataKeysHMI.MQTT_BROKER_SETTINGS,
                JSON.stringify(clusterStore.mqttBrokerSettings),
            );
            if (hmiCompressedData?.lastTwoShiftsEventReports) {
                localStorage.setItem(
                    LocalStorageCachedDataKeysHMI.LAST_TWO_SHIFT_EVENTS,
                    hmiCompressedData.lastTwoShiftsEventReports,
                );
            } else {
                setLocalStorageItem(
                    LocalStorageCachedDataKeysHMI.LAST_TWO_SHIFT_EVENTS,
                    JSON.stringify(last_two_shifts_event_reports),
                );
            }
            setStore((prev) => ({
                ...prev,
                ...cachedStore,
            }));
        } catch (error) {
            throw new Error(`Failed to prepare localstorage in move from cluster: ${error}`);
        }
    }, []);

    const trackEventEdition = (eventId: number, data: IEventsEditionsPending[0]) => {
        setStore((prevStore) => {
            const updateEditionsPending = {
                ...prevStore.editionsPending,
                [eventId]: data,
            };

            setLocalStorageItem(LocalStorageCachedDataKeysHMI.EDITIONS_PENDING, JSON.stringify(updateEditionsPending));
            return {
                ...prevStore,
                editionsPending: updateEditionsPending,
            };
        });
    };

    const trackPendingManualQuality = (entry: IManualQualityPendingEntry) => {
        setStore((prevStore) => {
            const key = buildManualQualityPendingKey(entry.machineCenterId, entry.hourAlignedIntervalStartIso);
            const updated = { ...prevStore.manualQualityPending, [key]: entry };
            setLocalStorageItem(LocalStorageCachedDataKeysHMI.MANUAL_QUALITY_PENDING, JSON.stringify(updated));
            return { ...prevStore, manualQualityPending: updated };
        });
    };

    const markPendingManualQualityAcked = (key: string) => {
        setStore((prevStore) => {
            const existing = prevStore.manualQualityPending[key];
            if (!existing || existing.mqttAcked) {
                return prevStore;
            }
            const updated = { ...prevStore.manualQualityPending, [key]: { ...existing, mqttAcked: true } };
            setLocalStorageItem(LocalStorageCachedDataKeysHMI.MANUAL_QUALITY_PENDING, JSON.stringify(updated));
            return { ...prevStore, manualQualityPending: updated };
        });
    };

    const clearPendingManualQuality = (key: string) => {
        setStore((prevStore) => {
            if (!(key in prevStore.manualQualityPending)) {
                return prevStore;
            }
            const { [key]: _removed, ...rest } = prevStore.manualQualityPending;
            setLocalStorageItem(LocalStorageCachedDataKeysHMI.MANUAL_QUALITY_PENDING, JSON.stringify(rest));
            return { ...prevStore, manualQualityPending: rest };
        });
    };

    const token = isTokenAvailable();
    const machineCenter = getMachineCenterIdHMI();
    const location = useLocation();
    const clusterId = getClusterIdHMI();

    const isHMIDashboard = useMemo(
        () => location.pathname.toLowerCase() === `/${HMI_PATHS.ROOT_PATH.toLowerCase()}/${HMI_PATHS.DASHBOARD}`,
        [location.pathname],
    );

    const getHMIData = useCallback(async () => {
        if (token && machineCenter?.machine_center_id) await GetData(machineCenter.machine_center_id);
    }, [GetData, machineCenter?.machine_center_id, token]);

    useEffect(() => {
        if (isHMIDashboard) {
            getHMIData();
            const interval = setInterval(async () => {
                try {
                    await getHMIData();
                } catch {}
            }, UPDATEMINUTESRATEFORHMIDATA);

            return () => clearInterval(interval);
        }
    }, [isHMIDashboard, getHMIData]);

    useHMIHeartbeatSignal({
        mode: HeartbeatMode.SINGLE,
        machineCenter: store.machineCenter,
        mqttBrokerSettings: store.mqttBrokerSettings?.[0],
        enabled: isHMIDashboard && !!store.machineCenter && !!store.mqttBrokerSettings?.[0] && !clusterId,
    });

    const providerValues = useMemo(
        () => ({
            store,
            dispatch: {
                GetData,
                resetStore,
                editEventReportData,
                isEventReportEditing,
                handleMoveToCluster,
                trackEventEdition,
                trackPendingManualQuality,
                markPendingManualQualityAcked,
                clearPendingManualQuality,
            },
        }),
        [store, GetData, resetStore, editEventReportData, isEventReportEditing, handleMoveToCluster],
    );

    return <HMICachedDataContext.Provider value={providerValues}>{children}</HMICachedDataContext.Provider>;
};
